import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PropertyInfoForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PropertyInfoForm() {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [formData, setFormData] = useState({
    propertyDetails: {
      type: 'residential',
      squareFootage: '',
      bedrooms: '',
      bathrooms: '',
      yearBuilt: '',
      lotSize: '',
      propertyStyle: ''
    },
    features: {
      pool: false,
      view: false,
      fireplace: false,
      updatedKitchen: false,
      updatedBathrooms: false,
      hardwoodFloors: false,
      granite: false,
      stainlessAppliances: false,
      smartHome: false,
      solar: false,
      other: []
    },
    shootPreferences: {
      areasToHighlight: [],
      areasToMinimize: [],
      specificShots: [],
      specialRequests: ''
    },
    access: {
      lockboxCode: '',
      keyLocation: '',
      gateCode: '',
      parkingInstructions: '',
      accessNotes: ''
    },
    preparation: {
      lightsOn: true,
      blindsOpen: true,
      decluttered: true,
      bedsMade: true,
      pets: {
        present: false,
        type: '',
        notes: ''
      },
      occupancy: 'vacant',
      preparationNotes: ''
    },
    safetyConsiderations: {
      dogs: false,
      constructionZone: false,
      stairs: false,
      lowCeilings: false,
      otherHazards: ''
    },
    listingInfo: {
      listPrice: '',
      mlsNumber: '',
      listingDate: '',
      openHouseDate: '',
      agentName: '',
      brokerage: ''
    },
    additionalServices: {
      virtualStaging: false,
      floorPlan: false,
      video: false,
      drone: false,
      twilight: false,
      notes: ''
    },
    notes: ''
  });

  const [highlightInput, setHighlightInput] = useState('');
  const [minimizeInput, setMinimizeInput] = useState('');
  const [shotInput, setShotInput] = useState('');
  const [otherFeatureInput, setOtherFeatureInput] = useState('');

  useEffect(() => {
    fetchAppointment();
  }, [appointmentId]);

  const fetchAppointment = async () => {
    try {
      const response = await axios.get(`${API_URL}/property-info/appointment/${appointmentId}?token=${token || ''}`);
      setAppointment(response.data.appointment);
      
      // If property info already exists, populate form
      if (response.data.propertyInfo) {
        setFormData(response.data.propertyInfo);
      } else if (response.data.appointment?.propertyType) {
        // Start the type picker at what was booked (a land booking was
        // showing "Residential").
        const bookedType = response.data.appointment.propertyType;
        setFormData(prev => ({ ...prev, propertyDetails: { ...prev.propertyDetails, type: bookedType } }));
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      alert('Could not load appointment details. Please check the link.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleCheckboxChange = (section, field) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const addToArray = (section, field, value, setInput) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: [...prev[section][field], value.trim()]
        }
      }));
      setInput('');
    }
  };

  const removeFromArray = (section, field, index) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/property-info/submit`, {
        appointmentId,
        token: token || '',
        ...formData
      });

      alert('Thank you! Your property information has been submitted successfully.');
      navigate('/property-info/success');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again or contact us.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="property-form-loading">
        <div className="spinner"></div>
        <p>Loading form...</p>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="property-form-error">
        <h2>Appointment Not Found</h2>
        <p>The appointment link may be invalid or expired.</p>
      </div>
    );
  }

  return (
    <div className="property-info-form-page">
      <div className="form-header">
        <h1>Property Information Form</h1>
        <p>Help us prepare for your photo shoot!</p>
        <div className="appointment-summary">
          <h3>Your Appointment:</h3>
          <p><strong>{appointment.package}</strong></p>
          <p>{new Date(appointment.appointmentDate).toLocaleDateString()} at {new Date(appointment.appointmentDate).toLocaleTimeString()}</p>
          <p>{appointment.propertyAddress}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="property-info-form">
        {/* Property Details */}
        <section className="form-section">
          <h2>Property Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Property Type *</label>
              <select
                value={formData.propertyDetails.type}
                onChange={(e) => handleInputChange('propertyDetails', 'type', e.target.value)}
                required
              >
                <option value="residential">Residential</option>
                <option value="luxury">Luxury Home</option>
                <option value="commercial">Commercial</option>
                <option value="land">Land</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Square Footage</label>
              <input
                type="number"
                value={formData.propertyDetails.squareFootage}
                onChange={(e) => handleInputChange('propertyDetails', 'squareFootage', e.target.value)}
                placeholder="2,500"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bedrooms</label>
              <input
                type="number"
                value={formData.propertyDetails.bedrooms}
                onChange={(e) => handleInputChange('propertyDetails', 'bedrooms', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                step="0.5"
                value={formData.propertyDetails.bathrooms}
                onChange={(e) => handleInputChange('propertyDetails', 'bathrooms', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Year Built</label>
              <input
                type="number"
                value={formData.propertyDetails.yearBuilt}
                onChange={(e) => handleInputChange('propertyDetails', 'yearBuilt', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="form-section">
          <h2>Property Features</h2>
          <div className="checkbox-grid">
            {Object.keys(formData.features).filter(k => k !== 'other').map(feature => (
              <label key={feature} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.features[feature]}
                  onChange={() => handleCheckboxChange('features', feature)}
                />
                <span>{feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
              </label>
            ))}
          </div>

          <div className="form-group">
            <label>Other Features</label>
            <div className="array-input">
              <input
                type="text"
                value={otherFeatureInput}
                onChange={(e) => setOtherFeatureInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToArray('features', 'other', otherFeatureInput, setOtherFeatureInput);
                  }
                }}
                placeholder="Add a feature and press Enter"
              />
              <button
                type="button"
                onClick={() => addToArray('features', 'other', otherFeatureInput, setOtherFeatureInput)}
              >
                Add
              </button>
            </div>
            <div className="tags-list">
              {formData.features.other.map((item, idx) => (
                <span key={idx} className="tag">
                  {item}
                  <button type="button" onClick={() => removeFromArray('features', 'other', idx)}>×</button>
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Shoot Preferences */}
        <section className="form-section">
          <h2>Photography Preferences</h2>
          
          <div className="form-group">
            <label>Areas to Highlight</label>
            <p className="help-text">What features should we emphasize? (e.g., "Kitchen remodel", "Mountain view")</p>
            <div className="array-input">
              <input
                type="text"
                value={highlightInput}
                onChange={(e) => setHighlightInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToArray('shootPreferences', 'areasToHighlight', highlightInput, setHighlightInput);
                  }
                }}
                placeholder="Add an area and press Enter"
              />
              <button
                type="button"
                onClick={() => addToArray('shootPreferences', 'areasToHighlight', highlightInput, setHighlightInput)}
              >
                Add
              </button>
            </div>
            <div className="tags-list">
              {formData.shootPreferences.areasToHighlight.map((item, idx) => (
                <span key={idx} className="tag highlight-tag">
                  {item}
                  <button type="button" onClick={() => removeFromArray('shootPreferences', 'areasToHighlight', idx)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Areas to Minimize</label>
            <p className="help-text">Any areas we should avoid highlighting? (e.g., "Old carpet", "Outdated bathroom")</p>
            <div className="array-input">
              <input
                type="text"
                value={minimizeInput}
                onChange={(e) => setMinimizeInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToArray('shootPreferences', 'areasToMinimize', minimizeInput, setMinimizeInput);
                  }
                }}
                placeholder="Add an area and press Enter"
              />
              <button
                type="button"
                onClick={() => addToArray('shootPreferences', 'areasToMinimize', minimizeInput, setMinimizeInput)}
              >
                Add
              </button>
            </div>
            <div className="tags-list">
              {formData.shootPreferences.areasToMinimize.map((item, idx) => (
                <span key={idx} className="tag minimize-tag">
                  {item}
                  <button type="button" onClick={() => removeFromArray('shootPreferences', 'areasToMinimize', idx)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Specific Shots Requested</label>
            <p className="help-text">Any specific angles or shots you want? (e.g., "Backyard from deck", "Street view")</p>
            <div className="array-input">
              <input
                type="text"
                value={shotInput}
                onChange={(e) => setShotInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToArray('shootPreferences', 'specificShots', shotInput, setShotInput);
                  }
                }}
                placeholder="Add a shot and press Enter"
              />
              <button
                type="button"
                onClick={() => addToArray('shootPreferences', 'specificShots', shotInput, setShotInput)}
              >
                Add
              </button>
            </div>
            <div className="tags-list">
              {formData.shootPreferences.specificShots.map((item, idx) => (
                <span key={idx} className="tag">
                  {item}
                  <button type="button" onClick={() => removeFromArray('shootPreferences', 'specificShots', idx)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Special Requests or Notes</label>
            <textarea
              value={formData.shootPreferences.specialRequests}
              onChange={(e) => handleInputChange('shootPreferences', 'specialRequests', e.target.value)}
              rows="4"
              placeholder="Any other photography preferences or requests..."
            />
          </div>
        </section>

        {/* Access Information */}
        <section className="form-section">
          <h2>Property Access</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Lockbox Code</label>
              <input
                type="text"
                value={formData.access.lockboxCode}
                onChange={(e) => handleInputChange('access', 'lockboxCode', e.target.value)}
                placeholder="1234"
              />
            </div>

            <div className="form-group">
              <label>Key Location</label>
              <input
                type="text"
                value={formData.access.keyLocation}
                onChange={(e) => handleInputChange('access', 'keyLocation', e.target.value)}
                placeholder="Under doormat"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gate Code</label>
              <input
                type="text"
                value={formData.access.gateCode}
                onChange={(e) => handleInputChange('access', 'gateCode', e.target.value)}
                placeholder="5678"
              />
            </div>

            <div className="form-group">
              <label>Parking Instructions</label>
              <input
                type="text"
                value={formData.access.parkingInstructions}
                onChange={(e) => handleInputChange('access', 'parkingInstructions', e.target.value)}
                placeholder="Driveway or street"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Additional Access Notes</label>
            <textarea
              value={formData.access.accessNotes}
              onChange={(e) => handleInputChange('access', 'accessNotes', e.target.value)}
              rows="3"
              placeholder="Any other access information we should know..."
            />
          </div>
        </section>

        {/* Preparation */}
        <section className="form-section">
          <h2>Property Preparation</h2>
          
          <div className="info-box">
            <h4>✅ Preparation Checklist</h4>
            <p>Please confirm these will be done before the shoot:</p>
          </div>

          <div className="checkbox-list">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.preparation.lightsOn}
                onChange={() => handleCheckboxChange('preparation', 'lightsOn')}
              />
              <span>All lights turned ON (inside and outside)</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.preparation.blindsOpen}
                onChange={() => handleCheckboxChange('preparation', 'blindsOpen')}
              />
              <span>All blinds and curtains OPEN</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.preparation.decluttered}
                onChange={() => handleCheckboxChange('preparation', 'decluttered')}
              />
              <span>Property decluttered (counters cleared, personal items hidden)</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.preparation.bedsMade}
                onChange={() => handleCheckboxChange('preparation', 'bedsMade')}
              />
              <span>All beds made with fresh linens</span>
            </label>
          </div>

          <div className="form-group">
            <label>Occupancy Status *</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="vacant"
                  checked={formData.preparation.occupancy === 'vacant'}
                  onChange={(e) => handleInputChange('preparation', 'occupancy', e.target.value)}
                />
                <span>Vacant</span>
              </label>
              <label>
                <input
                  type="radio"
                  value="occupied"
                  checked={formData.preparation.occupancy === 'occupied'}
                  onChange={(e) => handleInputChange('preparation', 'occupancy', e.target.value)}
                />
                <span>Occupied</span>
              </label>
              <label>
                <input
                  type="radio"
                  value="staged"
                  checked={formData.preparation.occupancy === 'staged'}
                  onChange={(e) => handleInputChange('preparation', 'occupancy', e.target.value)}
                />
                <span>Professionally Staged</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.preparation.pets.present}
                onChange={() => handleCheckboxChange('preparation', 'pets', 'present')}
              />
              <span>Pets will be on property during shoot</span>
            </label>
            
            {formData.preparation.pets.present && (
              <>
                <input
                  type="text"
                  value={formData.preparation.pets.type}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    preparation: {
                      ...prev.preparation,
                      pets: { ...prev.preparation.pets, type: e.target.value }
                    }
                  }))}
                  placeholder="Type of pet (dog, cat, etc.)"
                  className="mt-2"
                />
                <textarea
                  value={formData.preparation.pets.notes}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    preparation: {
                      ...prev.preparation,
                      pets: { ...prev.preparation.pets, notes: e.target.value }
                    }
                  }))}
                  rows="2"
                  placeholder="Pet behavior notes (friendly, needs to be secured, etc.)"
                  className="mt-2"
                />
              </>
            )}
          </div>

          <div className="form-group">
            <label>Preparation Notes</label>
            <textarea
              value={formData.preparation.preparationNotes}
              onChange={(e) => handleInputChange('preparation', 'preparationNotes', e.target.value)}
              rows="3"
              placeholder="Any other preparation notes..."
            />
          </div>
        </section>

        {/* Safety Considerations */}
        <section className="form-section">
          <h2>Safety Considerations</h2>
          <p className="help-text">Help us stay safe while photographing your property</p>
          
          <div className="checkbox-grid">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.safetyConsiderations.dogs}
                onChange={() => handleCheckboxChange('safetyConsiderations', 'dogs')}
              />
              <span>Dogs on Property</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.safetyConsiderations.constructionZone}
                onChange={() => handleCheckboxChange('safetyConsiderations', 'constructionZone')}
              />
              <span>Active Construction</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.safetyConsiderations.stairs}
                onChange={() => handleCheckboxChange('safetyConsiderations', 'stairs')}
              />
              <span>Steep/Narrow Stairs</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.safetyConsiderations.lowCeilings}
                onChange={() => handleCheckboxChange('safetyConsiderations', 'lowCeilings')}
              />
              <span>Low Ceilings</span>
            </label>
          </div>

          <div className="form-group">
            <label>Other Hazards or Concerns</label>
            <textarea
              value={formData.safetyConsiderations.otherHazards}
              onChange={(e) => handleInputChange('safetyConsiderations', 'otherHazards', e.target.value)}
              rows="3"
              placeholder="Any safety hazards we should know about..."
            />
          </div>
        </section>

        {/* Additional Notes */}
        <section className="form-section">
          <h2>Additional Information</h2>
          
          <div className="form-group">
            <label>Any Other Notes or Special Requests</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows="4"
              placeholder="Anything else we should know..."
            />
          </div>
        </section>

        {/* Submit */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Property Information'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PropertyInfoForm;
